// src/components/visitors/RegisterAircraftModal.js
import React, { useState, useRef, useEffect } from 'react';  // ← Ajoutez useEffect
import axios from 'axios';
import { X, Loader, CheckCircle, AlertCircle, UserPlus } from 'lucide-react';
import { Modal, Field, ModalFooter } from '../fuel/modalComponents';
import { 
  AcSection, AcLabel, EMPTY_AIRCRAFT_FORM, EMPTY_NEW_OWNER,
  lbsToKg, galToL, faaDateToISO 
} from './aircraftHelpers';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const RegisterAircraftModal = ({ 
  isOpen, 
  selectedVisitor, 
  clients, 
  onClose, 
  onSuccess,
  fetchOwners 
}) => {
  const [acForm, setAcForm] = useState(EMPTY_AIRCRAFT_FORM);
  const [savingAc, setSavingAc] = useState(false);
  const [lookupStatus, setLookupStatus] = useState(null);
  const lookupTimer = useRef(null);
  
  // New Owner sub-modal
  const [showNewOwnerModal, setShowNewOwnerModal] = useState(false);
  const [newOwnerForm, setNewOwnerForm] = useState(EMPTY_NEW_OWNER);
  const [savingOwner, setSavingOwner] = useState(false);
  const [ownerError, setOwnerError] = useState(null);

  // Définir triggerLookup AVANT de l'utiliser
  const triggerLookup = async (tail) => {
    setLookupStatus('loading');
    try {
      const { data } = await axios.get(`${API_BASE}/lookup/${tail}`);
      setAcForm(prev => ({
        ...prev,
        manufacturer: data.manufacturer || prev.manufacturer,
        model: data.model || prev.model,
        aircraft_type: data.aircraft_type || prev.aircraft_type,
        year_manufactured: data.year_manufactured || prev.year_manufactured,
        serial_number: data.serial_number || prev.serial_number,
        icao_code: data.icao_code || prev.icao_code,
        engine_type: data.engine_type || prev.engine_type,
        engine_manufacturer: data.engine_manufacturer || prev.engine_manufacturer,
        engine_model: data.engine_model || prev.engine_model,
        engine_count: data.engine_count || prev.engine_count,
        airworthiness_date: faaDateToISO(data.airworthiness_date) || prev.airworthiness_date,
        certificate_issue_date: faaDateToISO(data.certificate_issue_date) || prev.certificate_issue_date,
        max_takeoff_weight_lbs: data.max_takeoff_weight_lbs || prev.max_takeoff_weight_lbs,
        max_landing_weight_lbs: data.max_landing_weight_lbs || prev.max_landing_weight_lbs,
        owner_name: data.owner_name || prev.owner_name,
        owner_address: data.owner_address || prev.owner_address,
        owner_city: data.owner_city || prev.owner_city,
        owner_state: data.owner_state || prev.owner_state,
        owner_zip: data.owner_zip || prev.owner_zip,
        owner_country: data.owner_country || prev.owner_country,
        owner_phone: data.owner_phone || prev.owner_phone,
      }));
      setLookupStatus('found');
    } catch (err) {
      setLookupStatus(err.response?.status === 404 ? 'notfound' : 'error');
    }
  };

  // Auto-trigger lookup when modal opens with a tail number
  useEffect(() => {
    if (isOpen && selectedVisitor?.tail_number && selectedVisitor.tail_number.length >= 4) {
      setAcForm(prev => ({
        ...prev,
        tail_number: selectedVisitor.tail_number || '',
        callsign: selectedVisitor.callsign || '',
      }));
      // Déclencher la recherche FAA
      triggerLookup(selectedVisitor.tail_number);
    }
  }, [isOpen, selectedVisitor]);  // ← Se déclenche quand le modal s'ouvre

  if (!isOpen || !selectedVisitor) return null;

  const acField = (key, val) => setAcForm(prev => ({ ...prev, [key]: val }));

  const handleTailNumberChange = (value) => {
    const tail = value.toUpperCase();
    acField('tail_number', tail);
    setLookupStatus(null);
    if (tail.length < 4) return;
    clearTimeout(lookupTimer.current);
    lookupTimer.current = setTimeout(() => triggerLookup(tail), 800);
  };

  const handleSaveAircraft = async () => {
    if (!acForm.tail_number) { alert('Tail number is required.'); return; }
    setSavingAc(true);
    try {
      const lbs_mtow = acForm.max_takeoff_weight_lbs ? parseInt(acForm.max_takeoff_weight_lbs) : undefined;
      const lbs_mlw = acForm.max_landing_weight_lbs ? parseInt(acForm.max_landing_weight_lbs) : undefined;
      const gal_cap = acForm.fuel_capacity_gallons ? parseFloat(acForm.fuel_capacity_gallons) : undefined;
      const gal_max = acForm.max_fuel_gallons ? parseFloat(acForm.max_fuel_gallons) : undefined;

      const payload = {
        ...acForm,
        owner_id: acForm.owner_id || undefined,
        year: acForm.year ? parseInt(acForm.year) : undefined,
        year_manufactured: acForm.year_manufactured ? parseInt(acForm.year_manufactured) : undefined,
        seats: acForm.seats ? parseInt(acForm.seats) : undefined,
        engine_count: acForm.engine_count ? parseInt(acForm.engine_count) : undefined,
        airworthiness_date: acForm.airworthiness_date || undefined,
        certificate_issue_date: acForm.certificate_issue_date || undefined,
        max_takeoff_weight_lbs: lbs_mtow,
        max_takeoff_weight_kg: lbs_mtow ? lbsToKg(lbs_mtow) : undefined,
        max_landing_weight_lbs: lbs_mlw,
        max_landing_weight_kg: lbs_mlw ? lbsToKg(lbs_mlw) : undefined,
        mtow_kg: lbs_mtow ? lbsToKg(lbs_mtow) : undefined,
        fuel_capacity_gallons: gal_cap,
        fuel_capacity_liters: gal_cap ? galToL(gal_cap) : undefined,
        max_fuel_gallons: gal_max,
        max_fuel_liters: gal_max ? galToL(gal_max) : undefined,
      };

      await axios.post(`${API_BASE}/aircraft`, payload);
      onSuccess();
      onClose();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error saving aircraft');
    } finally {
      setSavingAc(false);
    }
  };

  // New Owner handlers
  const openNewOwnerModal = () => {
    setNewOwnerForm({
      email: acForm.owner_email || '',
      first_name: '',
      last_name: acForm.owner_name || '',
      phone: acForm.owner_phone || '',
    });
    setOwnerError(null);
    setShowNewOwnerModal(true);
  };

  const closeNewOwnerModal = () => {
    setShowNewOwnerModal(false);
    setNewOwnerForm(EMPTY_NEW_OWNER);
    setOwnerError(null);
  };

  const ownerField = (key, val) => setNewOwnerForm(prev => ({ ...prev, [key]: val }));

  const handleCreateOwner = async () => {
    if (!newOwnerForm.email) { setOwnerError('Email is required.'); return; }
    setSavingOwner(true);
    setOwnerError(null);
    try {
      const { data: newOwner } = await axios.post(`${API_BASE}/owners/`, newOwnerForm);
      if (fetchOwners) fetchOwners();
      acField('owner_id', newOwner.id);
      closeNewOwnerModal();
    } catch (err) {
      setOwnerError(err.response?.data?.detail || 'Could not create owner.');
    } finally {
      setSavingOwner(false);
    }
  };

  const LookupBadge = () => {
    if (!lookupStatus) return null;
    const map = {
      loading: { icon: <Loader size={14} className="animate-spin" />, text: 'Searching FAA…', color: 'text-blue-400' },
      found: { icon: <CheckCircle size={14} />, text: 'FAA data imported ✓', color: 'text-green-400' },
      notfound: { icon: <AlertCircle size={14} />, text: 'Not found — manual entry', color: 'text-yellow-400' },
      error: { icon: <AlertCircle size={14} />, text: 'Lookup error', color: 'text-red-400' },
    };
    const { icon, text, color } = map[lookupStatus];
    return <div className={`flex items-center gap-1 text-xs mt-1 ${color}`}>{icon} {text}</div>;
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-2 sm:p-4">
        <div className="bg-gray-800 rounded-xl w-full max-w-[95vw] sm:max-w-2xl shadow-2xl flex flex-col max-h-[95vh] sm:max-h-[90vh]">
          <div className="p-4 sm:p-6 border-b border-gray-700 flex justify-between items-center">
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-white">
                Register Aircraft — {selectedVisitor.tail_number}
              </h3>
              <p className="text-gray-400 text-xs mt-1 hidden sm:block">
                FAA data loads automatically from the tail number.
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white p-1">
              <X size={20} />
            </button>
          </div>

          <div className="p-4 sm:p-6 overflow-y-auto space-y-4 sm:space-y-5 flex-1">
            {/* Identification */}
            <AcSection title="Identification">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <AcLabel>Tail Number *</AcLabel>
                  <input type="text" placeholder="ex: N12345" value={acForm.tail_number}
                    onChange={e => handleTailNumberChange(e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  <LookupBadge />
                </div>
                {/* ... reste du formulaire inchangé ... */}
                <div>
                  <AcLabel>Manufacturer</AcLabel>
                  <input type="text" value={acForm.manufacturer}
                    onChange={e => acField('manufacturer', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                </div>
                <div>
                  <AcLabel>Model</AcLabel>
                  <input type="text" value={acForm.model}
                    onChange={e => acField('model', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                </div>
                <div>
                  <AcLabel>Aircraft Type</AcLabel>
                  <input type="text" value={acForm.aircraft_type}
                    onChange={e => acField('aircraft_type', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                </div>
                <div>
                  <AcLabel>Year Manufactured</AcLabel>
                  <input type="number" value={acForm.year_manufactured}
                    onChange={e => acField('year_manufactured', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                </div>
                <div>
                  <AcLabel>Serial Number</AcLabel>
                  <input type="text" value={acForm.serial_number}
                    onChange={e => acField('serial_number', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                </div>
                <div>
                  <AcLabel>ICAO Code</AcLabel>
                  <input type="text" placeholder="ex: C172" value={acForm.icao_code}
                    onChange={e => acField('icao_code', e.target.value.toUpperCase())}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                </div>
                <div>
                  <AcLabel>Callsign</AcLabel>
                  <input type="text" value={acForm.callsign}
                    onChange={e => acField('callsign', e.target.value.toUpperCase())}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                </div>
              </div>
            </AcSection>

            {/* Engine & Fuel */}
            <AcSection title="Engine & Fuel">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <AcLabel>Engine Type</AcLabel>
                  <input type="text" value={acForm.engine_type}
                    onChange={e => acField('engine_type', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                </div>
                <div>
                  <AcLabel>Engine Count</AcLabel>
                  <input type="number" value={acForm.engine_count}
                    onChange={e => acField('engine_count', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                </div>
                <div>
                  <AcLabel>Engine Manufacturer</AcLabel>
                  <input type="text" value={acForm.engine_manufacturer}
                    onChange={e => acField('engine_manufacturer', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                </div>
                <div>
                  <AcLabel>Engine Model</AcLabel>
                  <input type="text" value={acForm.engine_model}
                    onChange={e => acField('engine_model', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                </div>
                <div>
                  <AcLabel>Seats</AcLabel>
                  <input type="number" value={acForm.seats}
                    onChange={e => acField('seats', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                </div>
                <div>
                  <AcLabel>Fuel Type</AcLabel>
                  <select value={acForm.fuel_type} onChange={e => acField('fuel_type', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm">
                    <option value="100LL">100LL (Avgas)</option>
                    <option value="JetA">Jet-A</option>
                    <option value="JetA_plus">Jet-A Plus</option>
                  </select>
                </div>
                <div>
                  <AcLabel>Fuel Capacity (US gal)</AcLabel>
                  <input type="number" step="0.1" value={acForm.fuel_capacity_gallons}
                    onChange={e => acField('fuel_capacity_gallons', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  {acForm.fuel_capacity_gallons > 0 && (
                    <p className="text-gray-500 text-xs mt-0.5">≈ {galToL(parseFloat(acForm.fuel_capacity_gallons))} L</p>
                  )}
                </div>
                <div>
                  <AcLabel>Max Fuel (US gal)</AcLabel>
                  <input type="number" step="0.1" value={acForm.max_fuel_gallons}
                    onChange={e => acField('max_fuel_gallons', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  {acForm.max_fuel_gallons > 0 && (
                    <p className="text-gray-500 text-xs mt-0.5">≈ {galToL(parseFloat(acForm.max_fuel_gallons))} L</p>
                  )}
                </div>
              </div>
            </AcSection>

            {/* Weights */}
            <AcSection title="Weights (US lbs)">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <AcLabel>Max Takeoff Weight (lbs)</AcLabel>
                  <input type="number" value={acForm.max_takeoff_weight_lbs}
                    onChange={e => acField('max_takeoff_weight_lbs', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  {acForm.max_takeoff_weight_lbs > 0 && (
                    <p className="text-gray-500 text-xs mt-0.5">
                      ≈ {lbsToKg(parseInt(acForm.max_takeoff_weight_lbs)).toLocaleString()} kg
                    </p>
                  )}
                </div>
                <div>
                  <AcLabel>Max Landing Weight (lbs)</AcLabel>
                  <input type="number" value={acForm.max_landing_weight_lbs}
                    onChange={e => acField('max_landing_weight_lbs', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  {acForm.max_landing_weight_lbs > 0 && (
                    <p className="text-gray-500 text-xs mt-0.5">
                      ≈ {lbsToKg(parseInt(acForm.max_landing_weight_lbs)).toLocaleString()} kg
                    </p>
                  )}
                </div>
              </div>
            </AcSection>

            {/* Owner & Position */}
            <AcSection title="Owner & Position">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <AcLabel>Owner (account)</AcLabel>
                  <div className="flex gap-2">
                    <select
                      value={acForm.owner_id}
                      onChange={e => acField('owner_id', e.target.value)}
                      className="flex-1 bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
                    >
                      <option value="">— No account linked —</option>
                      {clients.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.company_name || `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim() || c.email}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={openNewOwnerModal}
                      className="flex items-center gap-1.5 px-3 py-2 bg-green-700 hover:bg-green-600 text-white rounded-lg text-xs font-medium transition whitespace-nowrap flex-shrink-0"
                    >
                      <UserPlus size={14} /> New
                    </button>
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <AcLabel>Owner Name (registry)</AcLabel>
                  <input type="text" value={acForm.owner_name}
                    onChange={e => acField('owner_name', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                </div>
                <div className="sm:col-span-2">
                  <AcLabel>Street Address</AcLabel>
                  <input type="text" value={acForm.owner_address}
                    onChange={e => acField('owner_address', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                </div>
                <div>
                  <AcLabel>City</AcLabel>
                  <input type="text" value={acForm.owner_city}
                    onChange={e => acField('owner_city', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                </div>
                <div>
                  <AcLabel>State</AcLabel>
                  <input type="text" placeholder="TX" maxLength={2} value={acForm.owner_state}
                    onChange={e => acField('owner_state', e.target.value.toUpperCase())}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                </div>
                <div>
                  <AcLabel>ZIP Code</AcLabel>
                  <input type="text" value={acForm.owner_zip}
                    onChange={e => acField('owner_zip', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                </div>
                <div>
                  <AcLabel>Country</AcLabel>
                  <input type="text" value={acForm.owner_country}
                    onChange={e => acField('owner_country', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                </div>
                <div>
                  <AcLabel>Phone</AcLabel>
                  <input type="tel" value={acForm.owner_phone}
                    onChange={e => acField('owner_phone', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                </div>
                <div>
                  <AcLabel>Email</AcLabel>
                  <input type="email" value={acForm.owner_email}
                    onChange={e => acField('owner_email', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                </div>
                <div>
                  <AcLabel>Home Base</AcLabel>
                  <input type="text" placeholder="ex: KBOE" value={acForm.home_base}
                    onChange={e => acField('home_base', e.target.value.toUpperCase())}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                </div>
                <div>
                  <AcLabel>Parking Spot</AcLabel>
                  <input type="text" value={acForm.parking_spot}
                    onChange={e => acField('parking_spot', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                </div>
              </div>
            </AcSection>

            {/* Certificates */}
            <AcSection title="Certificates">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <AcLabel>Airworthiness Date</AcLabel>
                  <input type="date" value={acForm.airworthiness_date}
                    onChange={e => acField('airworthiness_date', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                </div>
                <div>
                  <AcLabel>Certificate Issue Date</AcLabel>
                  <input type="date" value={acForm.certificate_issue_date}
                    onChange={e => acField('certificate_issue_date', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                </div>
              </div>
            </AcSection>
          </div>

          <div className="p-4 sm:p-6 border-t border-gray-700 flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
            <button onClick={onClose}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm transition w-full sm:w-auto">
              Cancel
            </button>
            <button
              onClick={handleSaveAircraft}
              disabled={savingAc || !acForm.tail_number}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm transition w-full sm:w-auto"
            >
              {savingAc ? 'Saving…' : 'Save Aircraft'}
            </button>
          </div>
        </div>
      </div>

      {/* New Owner Sub-modal */}
      {showNewOwnerModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md shadow-2xl">
            <div className="p-5 border-b border-gray-700 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <UserPlus size={18} className="text-green-400" />
                <h3 className="text-white font-bold">New Owner</h3>
              </div>
              <button onClick={closeNewOwnerModal} className="text-gray-400 hover:text-white p-1">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              {ownerError && (
                <div className="bg-red-900/40 border border-red-700 text-red-300 text-xs rounded-lg px-3 py-2">
                  {ownerError}
                </div>
              )}
              <div>
                <AcLabel>Email *</AcLabel>
                <input type="email" placeholder="pilot@example.com" value={newOwnerForm.email}
                  onChange={e => ownerField('email', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" autoFocus />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <AcLabel>First Name</AcLabel>
                  <input type="text" value={newOwnerForm.first_name}
                    onChange={e => ownerField('first_name', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                </div>
                <div>
                  <AcLabel>Last Name</AcLabel>
                  <input type="text" value={newOwnerForm.last_name}
                    onChange={e => ownerField('last_name', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                </div>
              </div>
              <div>
                <AcLabel>Phone</AcLabel>
                <input type="tel" value={newOwnerForm.phone}
                  onChange={e => ownerField('phone', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              </div>
            </div>
            <div className="p-5 border-t border-gray-700 flex justify-end gap-2">
              <button onClick={closeNewOwnerModal}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm transition">
                Cancel
              </button>
              <button onClick={handleCreateOwner}
                disabled={savingOwner || !newOwnerForm.email}
                className="px-4 py-2 bg-green-700 hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm transition flex items-center gap-2"
              >
                {savingOwner
                  ? <><Loader size={14} className="animate-spin" /> Creating…</>
                  : <><UserPlus size={14} /> Create & Select</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RegisterAircraftModal;